function ownKeys(e, r) { var t = Object.keys(e); if (Object.getOwnPropertySymbols) { var o = Object.getOwnPropertySymbols(e); r && (o = o.filter(function (r) { return Object.getOwnPropertyDescriptor(e, r).enumerable; })), t.push.apply(t, o); } return t; }
function _objectSpread(e) { for (var r = 1; r < arguments.length; r++) { var t = null != arguments[r] ? arguments[r] : {}; r % 2 ? ownKeys(Object(t), !0).forEach(function (r) { _defineProperty(e, r, t[r]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(e, Object.getOwnPropertyDescriptors(t)) : ownKeys(Object(t)).forEach(function (r) { Object.defineProperty(e, r, Object.getOwnPropertyDescriptor(t, r)); }); } return e; }
function _defineProperty(e, r, t) { return (r = _toPropertyKey(r)) in e ? Object.defineProperty(e, r, { value: t, enumerable: !0, configurable: !0, writable: !0 }) : e[r] = t, e; }
function _toPropertyKey(t) { var i = _toPrimitive(t, "string"); return "symbol" == typeof i ? i : i + ""; }
function _toPrimitive(t, r) { if ("object" != typeof t || !t) return t; var e = t[Symbol.toPrimitive]; if (void 0 !== e) { var i = e.call(t, r || "default"); if ("object" != typeof i) return i; throw new TypeError("@@toPrimitive must return a primitive value."); } return ("string" === r ? String : Number)(t); }
import store from '../reducers/storeRegistration';
import notificationActions from '../actions/notificationActionCreators';
import { buildPendingSelection } from '../utils/cabinetCsvExportSelection';
import { EXPORT_SCOPE_FILTERED, EXPORT_SCOPE_SELECTION, collectExportSelectionIds, resolveExportScope } from '../utils/cabinetExportScope';
const CABINET_CSV_EXPORT_CONFIRM_MODAL_ID = 'cabinet_csv_export_confirm';

/**
 *  Narrow export to what the cabinet is currently showing.
 *
 *  The cabinet's live index query filters are sent as-is so the server resolves rows through
 *  the same query that built the list. A non-empty selection narrows further via `ids`, which
 *  the server-side list query already supports.
 */
const buildExportRequest = (transientContext = {}) => {
  const contextFilters = _objectSpread({}, transientContext.filters || {});
  const selectedIds = collectExportSelectionIds(transientContext.selection);
  return {
    filters: selectedIds.length ? _objectSpread(_objectSpread({}, contextFilters), {}, {
      ids: selectedIds
    }) : contextFilters,
    scope: resolveExportScope({
      selectionCount: selectedIds.length,
      filters: contextFilters
    })
  };
};
const buildEmptyResultMessage = scope => {
  if (scope === EXPORT_SCOPE_SELECTION) {
    return 'No exportable assets found in the current selection.';
  }
  if (scope === EXPORT_SCOPE_FILTERED) {
    return 'No exportable assets match the current filters.';
  }
  return 'No exportable assets found in this cabinet.';
};
const buildSelectedFormTypes = (selection = {}) => Object.entries(selection).map(([formTypeIdentifier, contentPaths]) => ({
  formTypeIdentifier,
  contentPaths
}));
const validateSelection = (selection = {}) => {
  const entries = Object.entries(selection);
  if (!entries.length) {
    return 'At least one asset type must be selected.';
  }
  const hasInvalidEntry = entries.some(([, contentPaths]) => !Array.isArray(contentPaths) || !contentPaths.length);
  if (hasInvalidEntry) {
    return 'Each selected asset type must have at least one column selected.';
  }
  return '';
};
const mapCountsToFormTypes = (counts = []) => counts.map(({
  formTypeIdentifier,
  formTypeName,
  count
}) => ({
  formTypeIdentifier,
  formTypeName,
  count
}));
const showColumnSelectionModal = ({
  formTypes,
  totalCount,
  loadColumnOptions
}) => new Promise(resolve => {
  const selectionRef = {
    selection: buildPendingSelection(formTypes),
    validationError: '',
    isExportReady: false,
    isLoadingColumns: false,
    hasNoExportableColumns: false,
    columnsByFormType: {}
  };
  const dismissAndResolve = selectedFormTypes => {
    store.dispatch(notificationActions.dismiss(CABINET_CSV_EXPORT_CONFIRM_MODAL_ID));
    resolve(selectedFormTypes);
  };
  const openModal = () => {
    store.dispatch(notificationActions.block({
      id: CABINET_CSV_EXPORT_CONFIRM_MODAL_ID,
      modalKind: 'cabinetCsvExportColumns',
      headerMessage: 'Export to CSV',
      formTypes,
      totalCount,
      selectionRef,
      loadColumnOptions,
      leftButtonLabel: 'Cancel',
      leftButtonAction: async () => dismissAndResolve(null),
      rightButtonLabel: selectionRef.isExportReady ? 'Export' : 'Loading columns...',
      rightButtonDisabled: !selectionRef.isExportReady,
      rightButtonAction: async () => {
        if (!selectionRef.isExportReady) {
          return;
        }
        const validationError = validateSelection(selectionRef.selection);
        if (validationError) {
          selectionRef.validationError = validationError;
          store.dispatch(notificationActions.dismiss(CABINET_CSV_EXPORT_CONFIRM_MODAL_ID));
          openModal();
          return;
        }
        dismissAndResolve(buildSelectedFormTypes(selectionRef.selection));
      }
    }, {
      autoClose: false,
      showCloseButton: false
    }));
  };
  openModal();
});
export const evaluate = async (entity, entityName, source, content, parentContentPath, transientContext, nativeFunctions, sourceEvaluator) => {
  try {
    const {
      filters,
      scope
    } = buildExportRequest(transientContext);
    const prepareResult = await sourceEvaluator({
      type: 'cabinetCsvExportCountBehavior',
      cabinetIdentifier: transientContext === null || transientContext === void 0 ? void 0 : transientContext.cabinetIdentifier,
      filters,
      tags: ['csvImport']
    });
    if (!(prepareResult !== null && prepareResult !== void 0 && prepareResult.success)) {
      await sourceEvaluator({
        type: 'validationErrorBehavior',
        errors: [(prepareResult === null || prepareResult === void 0 ? void 0 : prepareResult.message) || 'Unable to prepare csv export.']
      });
      return false;
    }
    const formTypes = mapCountsToFormTypes(prepareResult.counts);
    if (!prepareResult.totalCount || !formTypes.length) {
      store.dispatch(notificationActions.info({
        id: 'cabinet_csv_export_no_assets',
        headerMessage: 'CSV Export',
        message: buildEmptyResultMessage(scope)
      }));
      return false;
    }
    const loadColumnOptions = async formTypeIdentifiers => sourceEvaluator({
      type: 'cabinetCsvExportColumnOptionsBehavior',
      cabinetIdentifier: transientContext === null || transientContext === void 0 ? void 0 : transientContext.cabinetIdentifier,
      formTypeIdentifiers,
      tags: ['csvImport']
    });
    const selectedFormTypes = await showColumnSelectionModal({
      formTypes,
      totalCount: prepareResult.totalCount,
      loadColumnOptions
    });
    if (!selectedFormTypes) {
      return false;
    }
    store.dispatch(notificationActions.info({
      id: 'cabinet_csv_export_in_progress',
      headerMessage: 'CSV Export',
      message: 'Preparing forms for export, this can take a while for large cabinets...'
    }, {
      autoClose: false
    }));
    const exportResult = await sourceEvaluator({
      type: 'cabinetCsvExportBehavior',
      cabinetIdentifier: transientContext === null || transientContext === void 0 ? void 0 : transientContext.cabinetIdentifier,
      filters,
      selectedFormTypes,
      tags: ['csvImport']
    });
    store.dispatch(notificationActions.dismiss('cabinet_csv_export_in_progress'));
    if (!(exportResult !== null && exportResult !== void 0 && exportResult.success) || !exportResult.downloadUrl) {
      await sourceEvaluator({
        type: 'validationErrorBehavior',
        errors: [(exportResult === null || exportResult === void 0 ? void 0 : exportResult.message) || 'CSV export failed. Please try again.']
      });
      return false;
    }
    window.location.href = exportResult.downloadUrl;
    return exportResult;
  } catch (error) {
    store.dispatch(notificationActions.dismiss('cabinet_csv_export_in_progress'));
    await sourceEvaluator({
      type: 'validationErrorBehavior',
      errors: [(error === null || error === void 0 ? void 0 : error.message) || 'CSV export failed. Please try again.']
    });
    return false;
  }
};
export default {
  evaluate
};